import { Component, HostListener, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { PromptsService } from '../../../services/prompts.service';
import { Prompt } from '../../../interfaces/prompt.interface';
import { ModalService } from '../../../services/modal.service';

@Component({
  selector: 'app-prompts-gallery-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './prompts-gallery-modal.component.html',
  styleUrls: ['./prompts-gallery-modal.component.css']
})
export class PromptsGalleryModalComponent implements OnInit {
  private readonly addNewCategoryValue = '__add_new__';
  isLoading = false;
  prompts: Prompt[] = [];
  searchTerm = '';
  selectedTab: 'system' | 'my' = 'system';
  categoriesByTab: Record<'system' | 'my', string[]> = {
    system: ['All Categories'],
    my: ['All Categories']
  };
  selectedCategoriesByTab: Record<'system' | 'my', string[]> = {
    system: [],
    my: []
  };
  isCategoryDropdownOpen = false;

  // Add / Edit form state
  isEditing = false;
  editingPrompt: Prompt | null = null;
  newPromptText = '';
  selectedPromptCategory = '';
  newPromptCategoryName = '';
  isAddingNewPromptCategory = false;

  constructor(
    public activeModal: NgbActiveModal,
    private promptsService: PromptsService,
    private modalService: ModalService
  ) {}

  ngOnInit(): void {
    this.loadPrompts();
  }

  private getPromptType(): string {
    return this.selectedTab === 'system' ? 'S' : 'U';
  }

  private getCategoriesForActiveTab(): string[] {
    return this.categoriesByTab[this.selectedTab];
  }

  private getSelectedCategoriesForActiveTab(): string[] {
    return this.selectedCategoriesByTab[this.selectedTab];
  }

  private setSelectedCategoriesForActiveTab(categories: string[]): void {
    this.selectedCategoriesByTab = {
      ...this.selectedCategoriesByTab,
      [this.selectedTab]: categories
    };
  }

  private normalizeType(value: string | undefined): string {
    const raw = (value || '').trim().toUpperCase();
    if (raw === 'SYSTEM') return 'S';
    if (raw === 'USER' || raw === 'MY') return 'U';
    return raw;
  }

  private isNonEmptyString(value: string | undefined): value is string {
    return typeof value === 'string' && value.trim().length > 0;
  }

  isUserPrompt(prompt: Prompt): boolean {
    return this.normalizeType(prompt.type) === 'U';
  }

  get formCategories(): string[] {
    return this.getCategoriesForActiveTab().filter((c) => c !== 'All Categories');
  }

  get selectableCategories(): string[] {
    return this.formCategories;
  }

  get categoryDisplayText(): string {
    const selectedCategories = this.getSelectedCategoriesForActiveTab();
    if (selectedCategories.length === 0) {
      return 'All Categories';
    }
    if (selectedCategories.length === 1) {
      return selectedCategories[0];
    }
    return `${selectedCategories.length} categories selected`;
  }

  get isAddMode(): boolean {
    return !this.editingPrompt;
  }

  get canSavePrompt(): boolean {
    const hasText = this.newPromptText.trim().length > 0;
    const hasCategory = this.getSelectedPromptCategoryValue().length > 0;

    if (this.isAddMode) {
      return hasText && hasCategory;
    }

    return hasText || hasCategory;
  }

  loadPrompts(): void {
    this.isLoading = true;
    this.promptsService.getPrompts(this.getPromptType(), undefined, 1, 1000)
      .subscribe((res) => {
        const promptType = this.getPromptType();
        this.prompts = (res || []).filter((prompt) => this.normalizeType(prompt.type) === promptType);
        this.categoriesByTab = {
          ...this.categoriesByTab,
          [this.selectedTab]: [
          'All Categories',
          ...Array.from(new Set(this.prompts.map((p) => p.category).filter((v): v is string => this.isNonEmptyString(v))))
          ]
        };
        this.isLoading = false;
      }, () => {
        this.prompts = [];
        this.categoriesByTab = {
          ...this.categoriesByTab,
          [this.selectedTab]: ['All Categories']
        };
        this.isLoading = false;
      });
  }

  get filteredPrompts(): Prompt[] {
    const term = this.searchTerm.trim().toLowerCase();
    const selectedCategories = this.getSelectedCategoriesForActiveTab();
    const categoryFiltered = selectedCategories.length === 0
      ? this.prompts
      : this.prompts.filter((p) => selectedCategories.includes((p.category || '').trim()));
    if (!term) return categoryFiltered;
    return categoryFiltered.filter(p => p.text.toLowerCase().includes(term));
  }

  selectTab(tab: 'system' | 'my'): void {
    if (this.selectedTab === tab) return;
    this.selectedTab = tab;
    this.isCategoryDropdownOpen = false;
    this.loadPrompts();
  }

  onCategoryChange(): void {
    // filtering is handled client-side by filteredPrompts getter
  }

  toggleCategoryDropdown(): void {
    this.isCategoryDropdownOpen = !this.isCategoryDropdownOpen;
  }

  onToggleCategory(category: string, checked: boolean): void {
    const selectedCategories = this.getSelectedCategoriesForActiveTab();
    if (checked) {
      if (!selectedCategories.includes(category)) {
        this.setSelectedCategoriesForActiveTab([...selectedCategories, category]);
      }
      return;
    }
    this.setSelectedCategoriesForActiveTab(selectedCategories.filter((c) => c !== category));
  }

  isCategorySelected(category: string): boolean {
    return this.getSelectedCategoriesForActiveTab().includes(category);
  }

  clearCategorySelection(): void {
    this.setSelectedCategoriesForActiveTab([]);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target?.closest('.category-dropdown')) {
      this.isCategoryDropdownOpen = false;
    }
  }

  selectPrompt(prompt: Prompt): void {
    this.activeModal.close(prompt.text);
  }

  openAddNew(): void {
    this.isEditing = true;
    this.editingPrompt = null;
    this.newPromptText = '';
    this.selectedPromptCategory = '';
    this.newPromptCategoryName = '';
    this.isAddingNewPromptCategory = false;
  }

  openEdit(prompt: Prompt): void {
    if (!this.isUserPrompt(prompt)) return;
    this.isEditing = true;
    this.editingPrompt = { ...prompt };
    this.newPromptText = prompt.text;
    this.selectedPromptCategory = prompt.category || '';
    this.newPromptCategoryName = '';
    this.isAddingNewPromptCategory = false;
  }

  cancelEdit(): void {
    this.isEditing = false;
    this.editingPrompt = null;
    this.newPromptText = '';
    this.selectedPromptCategory = '';
    this.newPromptCategoryName = '';
    this.isAddingNewPromptCategory = false;
  }

  savePrompt(): void {
    const text = this.newPromptText.trim();
    const category = this.getSelectedPromptCategoryValue();
    if (!this.canSavePrompt) return;

    if (this.editingPrompt) {
      if (!this.isUserPrompt(this.editingPrompt)) {
        this.cancelEdit();
        return;
      }
      this.promptsService.updatePrompt(this.editingPrompt.id, {
        text,
        type: this.normalizeType(this.editingPrompt.type),
        category
      }).subscribe((updated) => {
        if (updated) {
          const idx = this.prompts.findIndex(p => p.id === updated.id);
          if (idx >= 0) this.prompts[idx] = updated;
        }
        this.cancelEdit();
      });
    } else {
      this.promptsService.createPrompt(text, category).subscribe((created) => {
        if (created) {
          if (this.normalizeType(created.type) === this.getPromptType()) {
            this.prompts.unshift(created);
          } else {
            this.loadPrompts();
          }
        }
        this.cancelEdit();
      });
    }
  }

  onPromptCategoryChange(value: string): void {
    if (value === this.addNewCategoryValue) {
      this.isAddingNewPromptCategory = true;
      this.selectedPromptCategory = value;
      return;
    }

    this.isAddingNewPromptCategory = false;
    this.selectedPromptCategory = value;
    this.newPromptCategoryName = '';
  }

  private getSelectedPromptCategoryValue(): string {
    if (this.selectedPromptCategory === this.addNewCategoryValue) {
      return this.newPromptCategoryName.trim();
    }
    return this.selectedPromptCategory.trim();
  }

  async deletePrompt(prompt: Prompt): Promise<void> {
    if (!this.isUserPrompt(prompt)) return;
    try {
      const confirmed = await this.modalService.openDeleteConfirmModal('Prompt', 'Delete Prompt', 'Are you sure you want to delete this prompt?');
      if (!confirmed) return;
      this.promptsService.deletePrompt(prompt.id).subscribe((ok) => {
        if (ok) {
          this.prompts = this.prompts.filter(p => p.id !== prompt.id);
        }
      });
    } catch {
      // cancelled
    }
  }

}
